/* eslint-disable filenames/match-regex */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { isEqual, trim } from 'lodash';

import dialogHandler from '../../global/lib/dialogHandler';
import { showUnsavedHoldReasonDialog } from '../../global/state_managers/DialogStateManager';

type Props = {
  setRouterWillLeaveValidator: Function,
  retryTransition: Function,
  initializeForm: Function,
  dirty: Boolean,
  values?: any,
  createLegalHoldReason: Function,
  resetLegalHoldReasons: Function,
  fields: {
    name: any,
    description: any,
  },
  flashMessages: {
    indicators: any[],
  },
  resetFlash: Function,
  registerUnmountCleanupMethod: Function,
  setBreadcrumbForHoldReasonsNewReasonPage: Function,

}

export function viewLogic() {}

export function stateLogic({ goToReasonsIndex }: any) {
  return function NewHoldReasonStateLogicComposer(component: any) {
    function NewHoldReasonStateLogic(props: Props) {
      const prevValuesRef = React.useRef(props.values);

      // Mount effect - setup
      React.useEffect(() => {
        props.setBreadcrumbForHoldReasonsNewReasonPage();
        props.setRouterWillLeaveValidator(routerWillLeave);

        // NOTE: no unmount cleanup resetting legalHoldReasons. The hold-reasons
        // list reloads reasons on every mount, so the reset was redundant — and
        // it's the same reset-on-unmount pattern that races a sibling page's load
        // under React 19 effect ordering (see HoldReasons.proxy.tsx). Dropping it
        // keeps this page consistent and race-free.
      }, []);

      // Update effect - reset flash when values change
      React.useEffect(() => {
        if (
          !isEqual(prevValuesRef.current, props.values) &&
          props.flashMessages.indicators.length
        ) {
          props.resetFlash({
            from: 'NewHoldReasonStateLogic/componentDidUpdate',
          });
        }
        prevValuesRef.current = props.values;
      }, [props.values, props.flashMessages.indicators.length]);

      const onSave = React.useCallback(() => {
        const { values } = props;
        props.initializeForm(values);
        props.createLegalHoldReason(values);
      }, [props.values, props.initializeForm, props.createLegalHoldReason]);

      const onCancel = React.useCallback(() => {
        goToReasonsIndex();
      }, []);

      const isValidForm = React.useMemo(() => {
        const {
          fields: { name, description },
        } = props;
        return !!(trim(name.value).length && trim(description.value).length);
      }, [props.fields.name.value, props.fields.description.value]);

      const routerWillLeave = React.useCallback(() => {
        if (!props.dirty) {
          return true;
        }
        dialogHandler(showUnsavedHoldReasonDialog, props.retryTransition);
        return false;
      }, [props.dirty, props.retryTransition]);

      return React.createElement(component, {
        ...props,
        onSave,
        onCancel,
        isValidForm,
        routerWillLeave,
      });
    }

    NewHoldReasonStateLogic.displayName = 'NewHoldReasonStateLogic';

    return NewHoldReasonStateLogic;
  };
}
